/**
 * De uitkomst van een telvraag: één getal, een reeks of een gestapelde reeks.
 *
 * <p>Welke weergave past leest dit onderdeel af aan de gegevens zelf, en niet aan een keuze die de
 * gebruiker eerst moet maken. Dat is wat het functioneel ontwerp bedoelt met "de gebruiker hoeft
 * niet eerst een rapporttype te kiezen": geen labels is één getal, één label een reeks, twee labels
 * een reeks per categorie.
 *
 * <p><b>De tabel staat er altijd onder.</b> Een staaf zonder getal is een plaatje; het ontwerp eist
 * dat elk cijfer terug te vinden is, en de eerste stap daarin is dat je het gewoon kunt lezen. De
 * staven zijn bewust in html en niet in een grafiekbibliotheek: dit zijn rechthoeken met een lengte,
 * en een bibliotheek erbij halen kost meer dan het oplevert — die keuze staat los van de vraag of er
 * later een echte grafiekmodule komt voor lijnen en verhoudingen.
 */

import { useState } from "react";
import {
  BarChart3,
  Download,
  FileSpreadsheet,
  Layers3,
  LineChart,
  Table2,
  TrendingUp,
} from "lucide-react";

import { csvDownload, csvGetal, csvTekst, exportKop } from "./csvExport";
import { kruistabelVan } from "./kruistabel";
import { StaafGrafiek, reeksenVan } from "./StaafGrafiek";

export interface SamenvattingGroep {
  labels: string[];
  /** Null als er niets te berekenen viel — bij een gemiddelde over uitsluitend lege waarden. */
  waarde: number | null;
  /** Hoeveel records buiten de berekening vielen omdat het veld leeg was. Nul bij een telling. */
  zonderWaarde?: number;
}

/** Wat er gerekend is. Bepaalt of er een totaal mag staan en hoeveel decimalen erbij horen. */
export type Maat = "AANTAL" | "GEMIDDELDE";

/**
 * Het totaal over alle groepen — alléén bij een telling.
 *
 * <p>Gemiddelden optellen levert een getal op dat nergens op slaat: de som van 43,5 en 21,0 zegt
 * niets over de dieren eronder. Het zou er wel net zo overtuigend staan als een echt totaal, en
 * daarom geeft dit null in plaats van een som waar het scherm dan zelf iets van moet vinden.
 */
export function totaalVan(groepen: SamenvattingGroep[], maat: Maat = "AANTAL"): number | null {
  if (maat !== "AANTAL") return null;
  return groepen.reduce((som, g) => som + (g.waarde ?? 0), 0);
}

/**
 * De reeksen voor een gestapelde weergave: per eerste label een rij, per tweede label een deel.
 *
 * <p>De volgorde van de eerste labels blijft zoals de server hem gaf — die sorteert op de
 * groepeerwaarde zelf, dus maanden staan op maandvolgorde. Opnieuw sorteren op het getal zou die
 * volgorde slopen en van een tijdreeks een ranglijst maken.
 */
export function stapelsVan(groepen: SamenvattingGroep[]): {
  rijen: { label: string; delen: { label: string; waarde: number | null }[]; totaal: number }[];
  categorieen: string[];
} {
  const rijen: { label: string; delen: { label: string; waarde: number | null }[]; totaal: number }[] = [];
  const categorieen: string[] = [];
  for (const g of groepen) {
    const eerste = g.labels[0] ?? "";
    const tweede = g.labels[1];
    let rij = rijen.find((r) => r.label === eerste);
    if (!rij) {
      rij = { label: eerste, delen: [], totaal: 0 };
      rijen.push(rij);
    }
    rij.delen.push({ label: tweede ?? "", waarde: g.waarde });
    rij.totaal += g.waarde ?? 0;
    if (tweede !== undefined && !categorieen.includes(tweede)) {
      categorieen.push(tweede);
    }
  }
  return { rijen, categorieen };
}

/** Hoe de uitkomst getoond wordt. Een manier van kijken, geen andere vraag. */
export type Weergave = "GESTAPELD" | "STAAF" | "LIJN" | "TABEL";

export function Samenvatting({
  groepen,
  groeperingen,
  titel,
  ondertitel,
  zonderTotaal = false,
  alsKaarten = false,
  maat = "AANTAL",
  totaal,
  cumulatief,
  onUitklappen,
  export: exportOpties,
  t,
}: {
  groepen: SamenvattingGroep[];
  groeperingen: string[];
  /**
   * De vorm van het analysescherm: een grafiekkaart met een weergaveschakelaar, en de cijfers in
   * een eigen kaart eronder.
   *
   * <p>Uit is de compacte vorm die de zoekresultaten gebruiken: één blok met de grafiek en de
   * tabel eronder. Dit onderdeel wordt door beide schermen gebruikt en die hebben nu eenmaal een
   * andere maat — een zoekresultaat is een tussenstap, een analyse is het eindproduct.
   */
  alsKaarten?: boolean;
  /**
   * Wat er boven de grafiek staat, in de balk van de grafiek zelf.
   *
   * <p>Bij een vast onderdeel is dat de naam ervan ("Intake: Aantal dieren binnengekomen"), zoals
   * in het scherm dat dit vervangt. Weglaten laat de assen zien — dat is wat een getypte vraag
   * krijgt, want die heeft geen naam.
   */
  titel?: string;
  /** Een regel onder de titel: waar de assen over gaan, of wat er precies geteld is. */
  ondertitel?: string;
  /**
   * Het totaal niet in de kop zetten.
   *
   * <p>Voor een scherm dat het al als kengetal boven de grafiek toont. Twee keer hetzelfde getal
   * op één scherm laat je zoeken naar het verschil, en dat is er niet.
   */
  zonderTotaal?: boolean;
  /**
   * Wat er gerekend is. Bepaalt of er een totaal boven mag staan: aantallen mag je optellen,
   * gemiddelden niet. Weglaten betekent een telling — dat was het altijd, tot er maten bij kwamen.
   */
  maat?: Maat;
  /**
   * Het totaal, als de getoonde cijfers er niet meer bij optellen.
   *
   * <p>Nodig bij een cumulatieve reeks: daar is elk cijfer al een lopend totaal, dus optellen
   * levert 13.396 + 13.536 = 26.932 op — een getal dat niet bestaat en er boven een grafiek net zo
   * overtuigend uitziet als een echt totaal. Weglaten betekent: de cijfers zijn los en mogen
   * gewoon opgeteld worden.
   */
  totaal?: number | null;
  /**
   * De schakelaar voor een oplopend totaal, naast de weergavekeuze.
   *
   * <p>Hier en niet in de filterbalk: cumulatief is een manier om dezelfde cijfers te tonen en
   * geen andere vraag, dus hij hoort bij de grafiek waarin je het ziet gebeuren. Weglaten laat de
   * schakelaar weg — bij een gemiddelde valt er niets op te tellen, en een schakelaar die niets
   * verandert laat je zoeken naar het verschil.
   *
   * <p>Het optellen doet dit onderdeel niet zelf: het krijgt de reeks die getoond moet worden. Zo
   * blijft er één plek waar bepaald wordt wat er in de grafiek staat.
   */
  cumulatief?: { aan: boolean; onZet: (aan: boolean) => void };
  /**
   * Doorklikken naar de records onder één groep. Weglaten maakt de rijen gewone tekst — handig voor
   * een voorbeeld of een export, en het scheelt een knop die nergens heen gaat.
   */
  onUitklappen?: (labels: string[]) => void;
  /**
   * Wat er in de kop van een export komt te staan: de vraag en de toegepaste filters.
   *
   * <p>Zonder dit is een los csv-bestand drie maanden later niet meer te plaatsen, en dan wordt er
   * gerekend met cijfers waarvan niemand meer weet waar ze over gingen. Ontbreekt het, dan is er
   * ook geen exportknop — een export zonder herkomst is erger dan geen export.
   */
  export?: {
    vraag: string;
    filters: string[];
    vestiging?: string | null;
    /**
     * Het Excel-bestand ophalen bij de server. Weglaten laat alleen de csv-knop staan.
     *
     * <p>Dit onderdeel haalt het bestand niet zelf op. Het weet wat er op het scherm staat, niet hoe
     * er met de server wordt gepraat — en zo blijft het testbaar zonder een netwerklaag na te bouwen.
     *
     * <p>Het krijgt de weergave mee die aan staat, zodat het bestand dezelfde grafiek tekent als het
     * scherm: wie een lijn koos, wil geen staven in zijn rapport.
     */
    onExcel?: (weergave: Weergave) => Promise<void>;
  };
  t: (key: string) => string;
}) {
  if (groepen.length === 0) {
    return null;
  }

  return <SamenvattingInhoud
    {...{ groepen, groeperingen, titel, ondertitel, zonderTotaal, alsKaarten, maat, totaal,
      cumulatief, onUitklappen, exportOpties, t }} />;
}

function SamenvattingInhoud({
  groepen,
  groeperingen,
  titel,
  ondertitel,
  zonderTotaal,
  alsKaarten,
  maat,
  totaal,
  cumulatief,
  onUitklappen,
  exportOpties,
  t,
}: {
  groepen: SamenvattingGroep[];
  groeperingen: string[];
  titel?: string;
  ondertitel?: string;
  zonderTotaal?: boolean;
  alsKaarten?: boolean;
  maat: Maat;
  totaal?: number | null;
  cumulatief?: { aan: boolean; onZet: (aan: boolean) => void };
  onUitklappen?: (labels: string[]) => void;
  exportOpties?: {
    vraag: string; filters: string[]; vestiging?: string | null; onExcel?: (weergave: Weergave) => Promise<void>;
  };
  t: (key: string) => string;
}) {
  // De haakjes staan hier en niet in Samenvatting zelf: daar staat een vroege return boven, en een
  // haakje na een return is een regel die React niet toestaat.
  const [bezig, setBezig] = useState(false);
  // Welke weergave er aan staat. Lokaal, want het is een manier van kijken en geen andere vraag:
  // omzetten hoeft niets opnieuw uit te rekenen.
  const [weergave, setWeergave] = useState<Weergave>("GESTAPELD");
  const [exportFout, setExportFout] = useState<string | null>(null);

  // Een telling is een geheel getal; een gemiddelde krijgt één decimaal. Een gemiddelde afronden
  // naar heel maakt van 0,4 en 1,4 allebei "1" en dat scheelt in een verslag een wereld.
  const getal = new Intl.NumberFormat("nl-NL", maat === "AANTAL"
    ? { maximumFractionDigits: 0 }
    : { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  /** Leeg blijft leeg: bij een gemiddelde over uitsluitend lege waarden valt er niets te tonen. */
  const toon = (w: number | null) => (w == null ? "—" : getal.format(w));
  // Hoeveel records nergens in meegeteld zijn. Dit hoort bij het cijfer: een gemiddelde over de
  // helft van de dieren is een ander cijfer dan waar het op lijkt.
  const zonderWaarde = groepen.reduce((som, g) => som + (g.zonderWaarde ?? 0), 0);

  // Geen uitsplitsing: één getal. Groot, want dat is het hele antwoord.
  if (groeperingen.length === 0) {
    return (
      <div className="fawkes-samenvatting fawkes-samenvatting--kpi">
        <span className="fawkes-samenvatting__getal">{toon(groepen[0].waarde)}</span>
        <span className="fawkes-samenvatting__bijschrift">
          {maat === "AANTAL"
            ? t("zoekresultaten.samenvatting.totaal")
            : t("zoekresultaten.samenvatting.gemiddelde")}
        </span>
        {zonderWaarde > 0 ? (
          <span className="fawkes-samenvatting__zonder">
            {zonderWaarde} {t("zoekresultaten.samenvatting.zonderWaarde")}
          </span>
        ) : null}
      </div>
    );
  }

  const exporteer = () => {
    if (!exportOpties) return;
    // Precies wat er in de tabel staat, in dezelfde volgorde: het ontwerp eist dat een export
    // dezelfde records bevat als de zichtbare tabel bij gelijke filters. Door van dezelfde
    // gegevens uit te gaan is dat geen belofte maar een gevolg.
    const rijenUit: (string | number | null)[][] = [
      ...exportKop({
        titel: "Data & Insight — samenvatting",
        vraag: exportOpties.vraag,
        filters: exportOpties.filters,
        uitsplitsing: groeperingen,
        vestiging: exportOpties.vestiging,
      }),
      [...groeperingen, maat === "AANTAL"
        ? t("zoekresultaten.samenvatting.aantal")
        : t("zoekresultaten.samenvatting.gemiddelde")],
      // Een lege waarde blijft leeg in het bestand: nul zou een gemiddelde van nul beweren.
      ...groepen.map((g) => [...g.labels, g.waarde == null ? "" : csvGetal(g.waarde)]),
    ];
    csvDownload(csvTekst(rijenUit),
      `data-insight-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const gestapeld = groeperingen.length > 1;

  const knoppen = (
    <>
      {exportOpties ? (
        <button type="button" className="fawkes-samenvatting__knop" onClick={exporteer}>
          <Download aria-hidden="true" />
          {t("zoekresultaten.samenvatting.exportCsv")}
        </button>
      ) : null}
      {exportOpties?.onExcel ? (
        <button
          type="button"
          className="fawkes-samenvatting__knop fawkes-samenvatting__knop--nadruk"
          disabled={bezig}
          onClick={() => {
            setExportFout(null);
            setBezig(true);
            // De server rekent de analyse opnieuw uit; bij een grote lijst duurt dat even. Zonder
            // deze stand klikt iemand drie keer en krijgt hij drie bestanden.
            void exportOpties
              .onExcel!(actief)
              .catch((e: unknown) =>
                setExportFout(e instanceof Error ? e.message : t("zoekresultaten.samenvatting.exportFout")),
              )
              .finally(() => setBezig(false));
          }}
        >
          <FileSpreadsheet aria-hidden="true" />
          {bezig
            ? t("zoekresultaten.samenvatting.exportBezig")
            : t("zoekresultaten.samenvatting.exportExcel")}
        </button>
      ) : null}
    </>
  );

  /**
   * De weergaveschakelaar.
   *
   * <p>Gestapeld en naast elkaar zijn allebei waar en lezen iets anders: gestapeld zie je het
   * totaal per periode en de verhouding erbinnen, naast elkaar vergelijk je de reeksen onderling.
   * Die keuze hoort bij wie kijkt, niet bij wie het scherm bouwt.
   *
   * <p>Zonder tweede as is er niets te stapelen: dan valt "gestapeld" weg in plaats van dat hij
   * hetzelfde plaatje geeft als "naast elkaar" — een knop die niets verandert laat je zoeken naar
   * het verschil.
   */
  const WEERGAVEN: { sleutel: Weergave; icoon: React.ReactNode; naam: string }[] = [
    ...(gestapeld
      ? [{ sleutel: "GESTAPELD" as const, icoon: <Layers3 />, naam: t("samenvatting.gestapeld") }]
      : []),
    { sleutel: "STAAF", icoon: <BarChart3 />, naam: t("samenvatting.staaf") },
    { sleutel: "LIJN", icoon: <LineChart />, naam: t("samenvatting.lijn") },
    { sleutel: "TABEL", icoon: <Table2 />, naam: t("samenvatting.tabel") },
  ];
  // Zonder tweede as bestaat "gestapeld" niet; stond hij aan, dan valt hij terug op staven.
  const actief: Weergave = weergave === "GESTAPELD" && !gestapeld ? "STAAF" : weergave;

  const schakelaar = (
    <div className="fawkes-samenvatting__weergaven" role="group"
      aria-label={t("samenvatting.weergave")}>
      {WEERGAVEN.map((w) => (
        <button
          key={w.sleutel}
          type="button"
          className={"fawkes-samenvatting__weergave"
            + (actief === w.sleutel ? " fawkes-samenvatting__weergave--aan" : "")}
          aria-pressed={actief === w.sleutel}
          onClick={() => setWeergave(w.sleutel)}
        >
          <span aria-hidden="true">{w.icoon}</span>
          {w.naam}
        </button>
      ))}
    </div>
  );

  const tabel = gestapeld ? (
    <table className="fawkes-samenvatting__tabel">
      <thead>
        <tr>
          <th>{groeperingen[1]}</th>
          {kruistabelVan(groepen).kolommen.map((k) => (
            <th className="r" key={k}>{k}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {kruistabelVan(groepen).rijen.map((rij) => (
          <tr key={rij.label}>
            <td>{rij.label}</td>
            {rij.cellen.map((cel, i) => (
              <td className="r" key={i}>
                {/* Een combinatie die niet voorkwam blijft leeg. Nul zou beweren dat er niets
                    was, terwijl de server er niets over zei. */}
                {cel == null ? "" : onUitklappen ? (
                  <button
                    type="button"
                    className="fawkes-samenvatting__doorklik"
                    onClick={() => onUitklappen(cel.labels)}
                    title={t("zoekresultaten.samenvatting.toonOnderliggend")}
                  >
                    {toon(cel.waarde)}
                  </button>
                ) : (
                  toon(cel.waarde)
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  ) : (
    <table className="fawkes-samenvatting__tabel">
      <thead>
        <tr>
          {groeperingen.map((g) => (
            <th key={g}>{g}</th>
          ))}
          <th className="r">{t("zoekresultaten.samenvatting.aantal")}</th>
        </tr>
      </thead>
      <tbody>
        {/* De tabelrij is de plek om op door te klikken: daar is één groep precies één rij, ook
            bij twee uitsplitsingen. Bij een gestapelde staaf zou een klik op het gekleurde deel
            moeten raden welk deel je bedoelde. */}
        {groepen.map((g, i) => (
          <tr key={g.labels.join("|") + i}>
            {g.labels.map((l, j) => (
              <td key={j}>{l}</td>
            ))}
            <td className="r">
              {onUitklappen ? (
                <button
                  type="button"
                  className="fawkes-samenvatting__doorklik"
                  onClick={() => onUitklappen(g.labels)}
                  title={t("zoekresultaten.samenvatting.toonOnderliggend")}
                >
                  {toon(g.waarde)}
                </button>
              ) : (
                toon(g.waarde)
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const grafiek = (
    <StaafGrafiek
      {...reeksenVan(groepen)}
      titel={titel || groeperingen.join(" · ")}
      asTitel={maat === "AANTAL"
        ? t("zoekresultaten.samenvatting.aantal")
        : t("zoekresultaten.samenvatting.gemiddelde")}
      soort={actief === "LIJN" ? "LIJN" : actief === "STAAF" ? "STAAF" : "GESTAPELD"}
      toon={toon}
    />
  );

  const kop = (
    <div className="fawkes-samenvatting__kop">
      {alsKaarten ? (
        <span className="fawkes-samenvatting__kopicoon" aria-hidden="true"><TrendingUp /></span>
      ) : null}
      <div className="fawkes-samenvatting__koptekst">
        {titel ? <h3 className="fawkes-samenvatting__titel">{titel}</h3> : null}
        {/* Waarop is uitgesplitst, met naam. Zonder dit moet je raden wat de as betekent — en
            het ontwerp eist juist dat zichtbaar is welke groepering is toegepast. */}
        <p className="fawkes-samenvatting__assen">
          {ondertitel ?? groeperingen.join(" · ")}
        </p>
        {/* Het totaal, tenzij het scherm het al als kengetal boven de grafiek toont. Alleen bij
            een telling: de som van gemiddelden is een getal dat nergens op slaat, en het zou er
            net zo overtuigend staan als een echt totaal. */}
        {zonderTotaal ? null : (totaal ?? totaalVan(groepen, maat)) != null ? (
          <span className="fawkes-samenvatting__totaal">
            {t("zoekresultaten.samenvatting.totaal")}:{" "}
            <strong>{toon(totaal ?? totaalVan(groepen, maat))}</strong>
          </span>
        ) : (
          <span className="fawkes-samenvatting__totaal">
            {t("zoekresultaten.samenvatting.gemiddelde")}
            {zonderWaarde > 0
              ? " · " + zonderWaarde + " " + t("zoekresultaten.samenvatting.zonderWaarde")
              : ""}
          </span>
        )}
      </div>
      <div className="fawkes-samenvatting__acties">
        {/* Cumulatief staat bij de weergavekeuze en niet in de filterbalk: het verandert hoe je
            dezelfde cijfers leest, niet welke cijfers het zijn. Bij een tabel blijft hij staan —
            een oplopende kolom is net zo goed een oplopend totaal. */}
        {alsKaarten && cumulatief ? (
          <label className={"fawkes-samenvatting__cumulatief"
            + (cumulatief.aan ? " fawkes-samenvatting__cumulatief--aan" : "")}>
            <input
              type="checkbox"
              checked={cumulatief.aan}
              onChange={(e) => cumulatief.onZet(e.target.checked)}
            />
            <TrendingUp aria-hidden="true" />
            {t("dataInsight.cumulatief")}
          </label>
        ) : null}
        {alsKaarten ? schakelaar : null}
        {knoppen}
      </div>
    </div>
  );

  // De compacte vorm: één blok, grafiek en tabel onder elkaar. Zo gebruiken de zoekresultaten hem.
  if (!alsKaarten) {
    return (
      <div className="fawkes-samenvatting">
        {kop}
        {exportFout ? <p className="fawkes-samenvatting__fout" role="alert">{exportFout}</p> : null}
        {grafiek}
        {tabel}
      </div>
    );
  }

  return (
    <>
      <section className="fawkes-samenvatting">
        {kop}
        {/* De reden van de server tonen en niet vervangen: die weet wélke groep niet klopte of
            waarom de analyse niet uitvoerbaar was. */}
        {exportFout ? <p className="fawkes-samenvatting__fout" role="alert">{exportFout}</p> : null}
        {actief === "TABEL" ? null : grafiek}
      </section>

      {/* De cijfers in een eigen kaart. Een staaf laat een verhouding zien; hier staat wat het is,
          en dat is geen bijlage bij de grafiek maar het antwoord zelf. */}
      <section className="fawkes-samenvatting fawkes-samenvatting--cijfers">
        <div className="fawkes-samenvatting__kop">
          <span className="fawkes-samenvatting__kopicoon" aria-hidden="true"><Table2 /></span>
          <div className="fawkes-samenvatting__koptekst">
            <h3 className="fawkes-samenvatting__titel">{t("samenvatting.onderliggend")}</h3>
            <p className="fawkes-samenvatting__assen">{groeperingen.join(" · ")}</p>
          </div>
        </div>
        {tabel}
      </section>
    </>
  );
}
